import { Badge, Group, Text } from "@mantine/core";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { WeeklyMaintenanceState, WeeklyVisitState } from "@msc/shared";

import { formatIsoDate } from "../../lib/date.js";

/** Reiter der Kundenseite, in den die Angaben führen. */
export type KundenReiter = "termine" | "systeme";

const AMPEL: Record<Exclude<WeeklyVisitState["status"], "none">, { farbe: string; text: string }> = {
  red: { farbe: "red", text: "pages.visits.reminderBadgeRed" },
  orange: { farbe: "orange", text: "pages.visits.reminderBadgeOrange" },
  green: { farbe: "green", text: "pages.visits.reminderBadgeGreen" },
  exempt: { farbe: "gray", text: "pages.visits.reminderBadgeExempt" },
};

/**
 * Vor-Ort-Termine und Wartung in der Kundenzeile (#304). Die Werte kommen
 * fertig aus dem Termin- und dem Wartungsmodul; hier wird nur dargestellt.
 * Der Link entsteht beim Aufrufer (`link`), damit die Komponente ohne Router
 * testbar bleibt. Fehlen beide Angaben (Weekly vor #304, ehemaliger Kunde),
 * erscheint nichts.
 */
export function RowVisitsMaintenance({
  visit,
  maintenance,
  link,
}: {
  visit: WeeklyVisitState | undefined;
  maintenance: WeeklyMaintenanceState | undefined;
  link: (reiter: KundenReiter, inhalt: ReactNode) => ReactNode;
}) {
  const { t } = useTranslation();
  if (!visit && !maintenance) return null;
  const ampel = visit && visit.status !== "none" ? AMPEL[visit.status] : null;

  return (
    <Group gap="xs">
      {visit && (
        <Group gap={4}>
          {link(
            "termine",
            <Text size="xs" c="dimmed" span>
              {`${t("weekly.visits.label")}: ${
                visit.lastVisitDate
                  ? t("weekly.visits.last", { date: formatIsoDate(visit.lastVisitDate) })
                  : t("weekly.visits.noneYet")
              } · ${
                visit.nextPlannedDate
                  ? t("weekly.visits.next", { date: formatIsoDate(visit.nextPlannedDate) })
                  : t("weekly.visits.nonePlanned")
              }`}
            </Text>,
          )}
          {ampel && (
            <Badge size="xs" color={ampel.farbe} variant="light">
              {t(ampel.text)}
            </Badge>
          )}
        </Group>
      )}
      {maintenance &&
        link(
          "systeme",
          maintenance.attention > 0 ? (
            <Badge
              size="xs"
              color={maintenance.red > 0 ? "red" : "orange"}
              variant="light"
              style={{ cursor: "pointer" }}
              title={t("weekly.maintenance.detail", {
                red: maintenance.red,
                orange: maintenance.orange,
                stale: maintenance.stale,
              })}
            >
              {t("weekly.maintenance.attention", { count: maintenance.attention })}
            </Badge>
          ) : (
            <Badge size="xs" color="gray" variant="light" style={{ cursor: "pointer" }}>
              {t("weekly.maintenance.ok")}
            </Badge>
          ),
        )}
    </Group>
  );
}
